<template>
	<ion-button
		v-if="!user.active"
		size="small"
		color="success"
		@click="activateUser"
	>
		{{ $t("common.activate") }}
	</ion-button>
	<ion-button v-else size="small" color="danger" @click="deactivateUser">
		{{ $t("common.deactivate") }}
	</ion-button>
</template>

<script setup lang="ts">
	import { alertController, IonButton } from "@ionic/vue";
	import { useSetUserActive } from "seedgreen-shared/queries/user.js";

	import { useI18n } from "vue-i18n";
	import { GVAUserPrivilegedDTO } from "seedgreen-shared/dtos/GVAUserDTO";
	import { showLoader } from "@/utils/loader";

	const props = defineProps<{
		user: GVAUserPrivilegedDTO;
	}>();

	const { t } = useI18n();

	const { setUserActive } = useSetUserActive();
	async function activateUser() {
		const alert = await alertController.create({
			header: t("user-management.activate-user-header"),
			message: t("user-management.activate-user-message"),
			buttons: [
				{
					text: t("common.cancel"),
					role: "cancel",
				},
				{
					text: t("common.activate"),
					handler: async () => await setActive(true),
				},
			],
		});
		await alert.present();
	}

	async function deactivateUser() {
		const alert = await alertController.create({
			header: t("user-management.deactivate-user-header"),
			subHeader: t("user-management.deactivate-user-subheader"),

			message: t("user-management.deactivate-user-message"),
			buttons: [
				{
					text: t("common.cancel"),
					role: "cancel",
				},
				{
					text: t("common.deactivate"),
					role: "destructive",
					handler: async () => await setActive(false),
				},
			],
		});
		await alert.present();
	}

	async function setActive(active: boolean) {
		const dismiss = await showLoader();
		try {
			await setUserActive({
				userId: props.user.id,
				active,
			});
		} finally {
			await dismiss();
		}
	}
</script>
